"use client";

import axios from "axios";
import { getAccess, setAccessToken } from "./tokens";
import { TERMS_VERSION } from "@/config/terms"; // for possible use in API calls

const API = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || "http://localhost:4000/api",
  timeout: 15000,
  withCredentials: false,
 // ✅ Prevent "Unexpected end of JSON input" on empty/non-JSON responses
  transformResponse: [
    (data, headers) => {
      if (data == null || data === "") return null; // empty body → null

      if (typeof data === "string") {
        // Try JSON, otherwise return raw string (HTML, text, etc.)
        try {
          return JSON.parse(data);
        } catch {
          return data;
        }
      }

      return data;
    },
  ],
});

const toStr = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (v instanceof Error) return v.message || "";
  if (typeof v === "object") {
    if (typeof v.error === "string") return v.error;
    if (typeof v.message === "string") return v.message;
    try { return JSON.stringify(v); } catch { return "[object]"; }
  }
  return String(v);
};

const normAxios = (err, fallback = "Request failed") => {
  const data = err?.response?.data;

  let msg =
    toStr(data?.error) ||
    toStr(data?.message) ||
    toStr(err?.message) ||
    fallback;

  // now safe:
  const low = msg.toLowerCase();

  if (
    low.includes("missing auth token") ||
    low.includes("unauthorized") ||
    low.includes("jwt expired") ||
    low.includes("jwt malformed") ||
    low.includes("invalid signature") ||
    low.includes("no token provided") ||
    low.includes("forbidden")
  ) {
    msg = "Please log in to continue.";
  }

  const e = new Error(msg);
  e.status = err?.response?.status;
  e.data = data;
  return e;
};


const makeQS = (obj = {}) =>
  new URLSearchParams(
    Object.entries(obj).flatMap(([k, v]) => {
      if (v === undefined || v === null || v === "") return [];
      if (Array.isArray(v)) return [[k, v.join(",")]];
      return [[k, v]];
    })
  ).toString();

// ------------------------------------------------------------------
// CLIENT-SAFE GUARDS (because this module uses window/localStorage/Pi)
// ------------------------------------------------------------------
const isBrowser = () => typeof window !== "undefined";

API.interceptors.request.use((config) => {
  if (!isBrowser()) return config;

  config.headers = config.headers || {};

  const raw = String(config.url || "");
  // strip origin if present
  const pathOnly = raw.replace(/^https?:\/\/[^/]+/i, "");
  // normalize leading slash
  const p = pathOnly.startsWith("/") ? pathOnly : `/${pathOnly}`;

  const isPiServerRoute =
    /^\/api\/pi\/|^\/pi\/|^\/api\/auth\/pi\/|^\/auth\/pi\//i.test(p) ||
    /^\/auth\/pi-login\b/i.test(p); // your custom endpoint

  if (isPiServerRoute) {
    const piToken = sessionStorage.getItem("piAccessToken");
    if (piToken) config.headers["X-Pi-Access-Token"] = piToken;
    else delete config.headers["X-Pi-Access-Token"];

    // Only remove Authorization if you're sure this endpoint is Pi-only
    delete config.headers.Authorization;
    delete config.headers.authorization;
  } else {
    const jwt = getAccess();
    if (jwt) config.headers.Authorization = `Bearer ${jwt}`;
    else delete config.headers.Authorization;
  }

  return config;
});

// ------------------------------------------------------------------
// Response interceptor (Pi 401 retry)
// ------------------------------------------------------------------
API.interceptors.response.use(
  (res) => res,
  async (error) => {
    const r = error?.response;
    const config = error?.config || {};

    if (!isBrowser()) return Promise.reject(error);

const isPiRoute = config.url?.includes("/pi/") || config.url?.includes("/auth/pi/");

    if (isPiRoute && r?.status === 401 && !config._piRetry) {
      config._piRetry = true;

      // OPTIONAL: only if you still want this escape hatch
      const authCtx = window.__authCtx;

     if (authCtx?.reauthPi) {
  const ok = await authCtx.reauthPi();
  if (ok) {
    const piToken = sessionStorage.getItem("piAccessToken");
    if (piToken) {
      config.headers = config.headers || {};
      config.headers["X-Pi-Access-Token"] = piToken;
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }
    return API.request(config);
  }
}
    }
    return Promise.reject(error);
  }
);

// -----------------------------
// Auth (Pi SDK + backend)
// -----------------------------
export async function loginWithPiAndBackend() {
  if (!isBrowser()) throw new Error("Not in browser.");

  // If you kept the CRA-style loader in app/layout, use it:
  if (window.__piReady) {
    const ready = await window.__piReady;
    if (!ready) throw new Error("Pi SDK not ready. Please try again.");
  }

  // Fallback: wait briefly for window.Pi if __piReady isn't present
  if (!window.Pi) {
    const start = Date.now();
    while (!window.Pi && Date.now() - start < 1500) {
      await new Promise((r) => setTimeout(r, 30));
    }
  }

  if (!window.Pi) throw new Error("Pi SDK not loaded. Use Pi Browser.");

  // Init once
  if (!window.__piInited) {
    try {
      window.Pi.init({ version: "2.0" });
      window.__piInited = true;
    } catch (e) {
      // ignore if already inited
      window.__piInited = true;
    }
  }

  const scopes = ["username", "payments"];

  const auth = await window.Pi.authenticate(scopes, (incompletePayment) => {
    console.warn("⚠️ Incomplete payment found:", incompletePayment);
  });

  if (!auth?.accessToken) throw new Error("No access token received from Pi.");

 sessionStorage.setItem("piAccessToken", auth.accessToken);

  const res = await API.post("/auth/pi-login", { accessToken: auth.accessToken });
  const data = res.data;

  setAccessToken(data.access);

 return {
  user: data.user,
  access: data.access,
  piToken: auth.accessToken,
};
}

// utils/api.js
export async function registerWithEmail(email, password, username) {
  const res = await API.post('/auth/register', { email, password, username });
  const data = res.data;

  setAccessToken(data.access);

  return { user: data.user, access: data.access };
}

export async function loginWithEmail(email, password) {
  const res = await API.post('/auth/login', { email, password });
  const data = res.data; // { success, user, access, refresh, jti }

  // Same token handling
  setAccessToken(data.access);

return { user: data.user, access: data.access };
}

export async function forgotPassword(email) {
  try {
    const normalizedEmail = String(email || "").toLowerCase().trim();

    const { data } = await API.post("/auth/forgot-password", {
      email: normalizedEmail,
    });

    return data; // { success, message }
  } catch (err) {
    throw normAxios(err, "Failed to send reset link");
  }
}

export async function resetPassword(token, password, confirmPassword) {
  try {
    if (!token) throw new Error("Missing reset token");

    const { data } = await API.post(
      `/auth/reset-password/${encodeURIComponent(token)}`,
      {
        password,
        confirmPassword,
      }
    );

    return data; // { success, message }
  } catch (err) {
    throw normAxios(err, "Failed to reset password");
  }
}
// -----------------------------
// Pi Payments (Pi token header)
// -----------------------------
const idem = (prefix, paymentId, extra = "") =>
  `${prefix}:${paymentId}${extra ? ":" + extra : ""}`;

export async function approvePiPayment({ paymentId, product }) {
  // Uses API instance so your interceptor adds the Pi token automatically
  const { data } = await API.post(
    "/pi/approve",
   { paymentId, product },
    { headers: { "Idempotency-Key": idem("approve", paymentId) } }
  );
  return data;
}

export async function completePiPayment({ paymentId, txid }) {
  const { data } = await API.post(
    "/pi/complete",
    { paymentId, txid },
    { headers: { "Idempotency-Key": idem("complete", paymentId, txid) } }
  );
  return data;
}

export async function cancelPiPayment({ paymentId }) {
  const { data } = await API.post(
    "/pi/cancel",
    { paymentId },
   { headers: { "Idempotency-Key": idem("cancel", paymentId) } }
  );
  return data;
}

// -----------------------------
// App APIs below (JWT by default via interceptor)
// Remove `await requirePiToken()` from app routes.
// -----------------------------

export async function sendFeedback({ rating, piUsername, userId, comment }) {
  const res = await API.post('/feedback', { rating, piUsername, userId, comment: comment || '' });
  return res.data;
}

// Vouchers
// Vouchers
export async function fetchVoucherOffers(filters = {}) {
  const params = new URLSearchParams();
  params.append('limit', filters.limit || 10);
  params.append('offset', filters.offset || 0);
  if (filters.brand) params.append('brand', filters.brand);
  if (filters.country) params.append('country', filters.country);
  if (filters.regions) params.append('regions', filters.regions);
  if (filters.subType) params.append('subType', filters.subType);
  const res = await API.get(`/vouchers/offers?${params.toString()}`);
  return res.data.list;
}

export async function purchaseVoucher(data) {
  const res = await API.post(`/transactions/voucher/purchase`, data);
  return res.data;
}

// Topups
export async function fetchTopupOffers({ limit, offset } = {}) {
  const params = new URLSearchParams();
  if (limit != null) params.append('limit', limit);
  if (offset != null) params.append('offset', offset);
  const res = await API.get(`/topups/offers?${params.toString()}`);
  return res.data.list;
}

export async function purchaseTopup(data) {
  const res = await API.post(`/transactions/topup/purchase`, data);
  return res.data;
}

// Balance
export async function fetchWalletBalance() {
  const res = await API.get(`/balance`);
  return res.data;
}

// eSIM me routes
export async function fetchMyEsimPurchases() {
  const { data } = await API.get('/me/esim/purchases');
  return data.list ?? [];
}

export async function fetchMyEsimPurchaseDetail(id) {
  const { data } = await API.get(`/me/esim/purchases/${encodeURIComponent(id)}`);
  return { order: data.order, meta: data.meta || {} };
}

export async function fetchMyEsimPurchaseQr(id) {
  const { data } = await API.get(`/me/esim/purchases/${encodeURIComponent(id)}/qrcode`);
  return data; // { ok, mime, base64, dataUri, lpa, iosInstall }
}

// eSIM catalog/admin
export async function fetchEsimOffers(filters = {}) {
  const qs = new URLSearchParams({ _limit: filters.limit ?? 20, _offset: filters.offset ?? 0 });
  if (filters.brand) qs.append('brand', filters.brand);
  if (filters.country) qs.append('country', filters.country);
  if (filters.subType) qs.append('subType', filters.subType);
  if (filters.regions) qs.append('regions', Array.isArray(filters.regions) ? filters.regions.join(',') : filters.regions);
  if (filters.q) qs.append('q', filters.q);
  const { data } = await API.get(`/esim/offers?${qs.toString()}`);
  return { list: data.list ?? [], total: data.total ?? 0, limit: data.limit ?? 20, offset: data.offset ?? 0 };
}

// Transactions (me)
export async function fetchMyTransactions() {
  const { data } = await API.get('/me/transactions');
  return Array.isArray(data) ? data
    : Array.isArray(data?.list) ? data.list
    : Array.isArray(data?.items) ? data.items
    : Array.isArray(data?.data?.items) ? data.data.items
    : [];
}

// Blocked transaction status — checks your DB first, then Zendit
export async function fetchTransactionStatus(txid) {
  try {
    const { data } = await API.get(`/disputes/status?txid=${encodeURIComponent(txid)}`);
    return data;
  } catch (err) {
    throw normAxios(err, 'Failed to fetch transaction status');
  }
}
// Pi check (Pi token)
export async function checkPaymentStatus(paymentId) {
  if (!paymentId) throw new Error("Missing paymentId");
  const res = await API.get(`/pi/check/${paymentId}`); // interceptor will use Pi token for /pi/check
  return res.data;
}

  // --- MEDIA UPLOAD HELPERS (threads) ---
// utils/presign-normalizer.js
export function normalizePresignResponse(data) {
  // Good path: { url, signedHeaders, key, cdnUrl }
  if (data && typeof data === "object" && "url" in data) {
    if (!data.signedHeaders || typeof data.signedHeaders !== "object") data.signedHeaders = {};
    return data;
  }
  // Back-compat: { uploadUrl, key, cdnUrl } (string or object)
  if (data && typeof data === "object" && "uploadUrl" in data) {
    const up = data.uploadUrl;
    if (typeof up === "string") {
      return { url: up, signedHeaders: {}, key: data.key, cdnUrl: data.cdnUrl };
    }
    if (up && typeof up === "object") {
      return { url: up.url, signedHeaders: up.signedHeaders || {}, key: data.key, cdnUrl: data.cdnUrl };
    }
  }
  throw new Error("Invalid presign response shape");
}


export async function uploadMarketFile({ listingId, kind = 'image', file, onProgress }) {
  const pre = await marketPresign({ listingId, kind, file });
  const contentType = file.type || "application/octet-stream";
  const signedHeaders = { "Content-Type": contentType, ...(pre.signedHeaders || {}) };
  await putToS3({ url: pre.url, signedHeaders, file, onProgress });
  const fin = await marketFinalize({ key: pre.key });
  return { key: pre.key, cdnUrl: fin.cdnUrl, bytes: fin.bytes, contentType: fin.contentType };
}
// --- Marketplace: single-part upload ---
export async function marketPresign({ listingId = 'draft', kind = 'image', file }) {
  if (!file) throw new Error('file is required');
  const contentType = file.type || "application/octet-stream";

  if (!(contentType.startsWith("image/") || contentType.startsWith("video/"))) {
    throw new Error(`Marketplace supports only images/videos. Got: ${contentType}`);
  }

  const { data } = await API.post('/market-media/presign', {
    listingId,
    kind,
    filename: file.name,
    contentType,
  }).catch((err) => { throw normAxios(err, 'Failed to prepare marketplace upload'); });

  return normalizePresignResponse(data);
}


export async function marketFinalize({ key }) {
  if (!key) throw new Error('key is required');
  const { data } = await API.post('/market-media/finalize', { key })
    .catch((err) => { throw normAxios(err, 'Failed to finalize marketplace upload'); });
  // { ok, key, cdnUrl, bytes, contentType }
  return data;
}

export async function marketDeleteObject({ key }) {
  if (!key) throw new Error('key is required');
  const { data } = await API.post('/market-media/delete', { key })
    .catch((err) => { throw normAxios(err, 'Failed to delete media'); });
  return data; // { ok: true }
}


// ask backend for a presigned PUT url
export async function presignThreadUpload({ threadId, file }) {
  if (!threadId || !file) throw new Error("threadId and file are required");
  const kind = file.type.startsWith("video/") ? "video" : "image";

  const { data } = await API.post("/media/presign", {
    threadId,
    kind,
    contentType: file.type,
    size: file.size,
    // sha256: await sha256Base64(file),
  }).catch((err) => { throw normAxios(err, "Failed to prepare upload"); });

  // Important: always return *something* (data) here.
  return data;
}

// tell backend to attach the uploaded object to the thread
export async function attachThreadMedia({ threadId, key, file, meta = {} }) {
  if (!threadId || !key || !file) throw new Error("threadId, key, and file are required");
  const type = file.type.startsWith("video/") ? "video" : "image";

  try {
    const { data } = await API.post(`/thread/${threadId}/media`, {
      key,
      type,
      contentType: file.type,
      size: file.size,
      width: meta.width,
      height: meta.height,
      duration: meta.duration,
    });
    // returns saved item: { key, url, type, ... }
    return data;
  } catch (err) {
    return normAxios(err, "Failed to attach media");
  }

}

// optional raw axios PUT to S3 (bypasses interceptors entirely)
// Raw PUT to S3 using presigned url + headers
// utils/put-to-s3.js
export async function putToS3({ url, signedHeaders = {}, file, onProgress }) {
  try {
    return await axios.put(url, file, {
      headers: signedHeaders, // MUST match what was signed
      onUploadProgress: (p) => {
        if (p.total && onProgress) onProgress(Math.round((p.loaded / p.total) * 100));
      },
      // Do not send credentials/cookies to S3
      withCredentials: false,
      // Make sure axios doesn't wrap the body
      transformRequest: [(data) => data],
      responseType: "text", // so we see the XML body on error
      validateStatus: (s) => s >= 200 && s < 300, // we'll catch in try/catch
    });
  } catch (err) {
    const status = err?.response?.status;
    const body = err?.response?.data; // S3 returns XML with <Code> and <Message>
    const msg = status === 403 ? `S3 403: ${body || "Forbidden"}` : (body || err.message);
    throw new Error(msg);
  }
}

// --- Multipart upload helpers ---
export async function mpuStart({ threadId, contentType }) {
  const { data } = await API.post("/media/mpu/start", { threadId, kind: "video", contentType });
  // { key, uploadId }
  return data;
}

export async function mpuPartUrls({ key, uploadId, parts }) {
  const { data } = await API.post("/media/mpu/part-urls", { key, uploadId, parts });
  // { urls: [{ partNumber, url }, ...] }
  return data.urls;
}

export async function mpuComplete({ key, uploadId, parts }) {
  // parts: [{ PartNumber, ETag }]
  const { data } = await API.post("/media/mpu/complete", { key, uploadId, parts });
  return data; // { ok, key }
}




// 🔹 DETAIL (joins Transaction + EsimTransaction)
export async function fetchMyTransactionDetail(id) {
  const res = await API.get(`/me/transactions/${encodeURIComponent(id)}`);
  return res.data; // { ok, _id, productType, status, deliveryStatus, to, esim:{...} }
}

// 🔹 USER QR (builds QR on server using joined eSIM)
export async function fetchMyTransactionQr(id) {
  const res = await API.get(`/me/transactions/${encodeURIComponent(id)}/qrcode`);
  return res.data; // { ok, mime, base64, dataUri }
}

// 🔹 USER usage by ICCID (if you expose a /me route for it)
export async function fetchMyEsimPlans(iccId) {
  const res = await API.get(`/me/esim/${encodeURIComponent(iccId)}/plans`);
  return { total: res.data.total ?? 0, list: res.data.list ?? [] };
}


export async function fetchEsimOfferById(offerId) {
  try {
    const { data } = await API.get(`/catalog/esim/offers/${encodeURIComponent(offerId)}`);
    return data.offer;
 } catch (err) {
  throw normAxios(err, "Failed to load eSIM offer");
}
}

// Create a purchase (useful for admin console/manual)
// Backend returns: { ok, status, transactionId }
export async function purchaseEsim({ offerId, iccid, transactionId } = {}) {
  try {
    if (!offerId) throw new Error("offerId is required");
    const { data } = await API.post("/esim/purchases", { offerId, iccid, transactionId });
    return data;
  } catch (err) {
   throw normAxios(err, "Failed to create eSIM purchase");
  }
}

// Optional: list purchases for an admin table
export async function fetchEsimPurchases({ limit = 20, offset = 0, status = "", offerId = "" } = {}) {
  try {
    const qs = makeQS({ _limit: limit, _offset: offset, status, offerId });
    const { data } = await API.get(`/esim/purchases?${qs}`);
    return data; // { ok, limit, offset, total, list:[...] }
  } catch (err) {
    normAxios(err, "Failed to load eSIM purchases");
  }
}

// Get one purchase/order (to read confirmation/ICCID/status)
export async function fetchEsimTransaction(transactionId) {
  try {
    const { data } = await API.get(`/esim/purchases/${encodeURIComponent(transactionId)}`);
    return data.order; // mapped order
  } catch (err) {
    normAxios(err, "Failed to load eSIM transaction");
  }
}

// Get QR (data URI by default)
export async function fetchEsimQRCode(transactionId) {
  try {
    const { data } = await API.get(
      `/esim/purchases/${encodeURIComponent(transactionId)}/qrcode`
    );
    // { ok, mime, base64, dataUri }
    return data;
  } catch (err) {
    normAxios(err, "Failed to load eSIM QR");
  }
}

export async function refundEsimPurchase(transactionId) {
  try {
    const { data } = await API.post(
      `/esim/purchases/${encodeURIComponent(transactionId)}/refund`
    );
    return data;
  } catch (err) {
    normAxios(err, "Failed to refund eSIM purchase");
  }
}

// Usage by ICCID
export async function fetchEsimPlans(iccId) {
  try {
    const { data } = await API.get(`/esim/${encodeURIComponent(iccId)}/plans`);
    return {
      total: data.total ?? 0,
      list: data.list ?? [],
    };
  } catch (err) {
    normAxios(err, "Failed to load eSIM usage");
  }
}

// Convenience: usage by transactionId (pull ICCID first)
export async function fetchEsimUsageByTransaction(transactionId) {
  const order = await fetchEsimTransaction(transactionId);
  const iccid = order?.confirmation?.iccid;
  if (!iccid) throw new Error("No ICCID available for this transaction yet.");
  return fetchEsimPlans(iccid);
}
// Promos
export async function fetchPromos() {
  const res = await API.get('/promos');
  return res.data;
}

export async function fetchPromoById(promoId) {
  const res = await API.get(`/promos/${promoId}`);
  return res.data;
}

//reports 
export async function requestTransactionReport(payload) {
  const res = await API.post('/reports/transactions', payload);
  return res.data;
}

export async function getTransactionReport(reportId) {
  const res = await API.get(`/reports/transactions/${reportId}`);
  return res.data;
}

export async function downloadTransactionFile(reportId, file) {
  const res = await API.get(`/reports/transactions/${reportId}/${file}`, {
    responseType: 'blob',
  });
  return res.data;
}
// pHoneLockup
export async function lookupPhoneNumber(msisdn) {
  const res = await API.get(`/tools/phonenumberlookup/${msisdn}`);
  return res.data;
}

// -----------------------------
// Dashboard stats (transactions/topups/esim)
// -----------------------------

// Summary KPIs: total revenue, total Pi, order count, date range
// GET /api/stats/summary?from=YYYY-MM-DD&to=YYYY-MM-DD&type=esim
export async function fetchStatsSummary(filters = {}) {
  try {
    const qs = makeQS(filters); // { from, to, type }
    const url = `/stats/summary${qs ? `?${qs}` : ''}`;
    const { data } = await API.get(url);
    // backend shape: { success: true, data: {...} }
    return data.data ?? data;
  } catch (err) {
    throw normAxios(err, 'Failed to load summary stats');
  }
}

// Top products: table / chart
// GET /api/stats/top-products?limit=10&from=...&to=...&type=voucher
export async function fetchStatsTopProducts(filters = {}) {
  try {
    const qs = makeQS(filters); // { limit, from, to, type }
    const url = `/stats/top-products${qs ? `?${qs}` : ''}`;
    const { data } = await API.get(url);
    // backend: { success: true, data: [...] }
    return data.data ?? data;
  } catch (err) {
    throw normAxios(err, 'Failed to load top products');
  }
}

// Geo stats: for world map by country
// GET /api/stats/geo?from=...&to=...&type=topup
export async function fetchStatsGeo(filters = {}) {
  try {
    const qs = makeQS(filters); // { from, to, type }
    const url = `/stats/geo${qs ? `?${qs}` : ''}`;
    const { data } = await API.get(url);
    // backend: { success: true, data: [...] }
    return data.data ?? data;
  } catch (err) {
    throw normAxios(err, 'Failed to load geo stats');
  }
}

// -----------------------------
// EBOOKS – PUBLIC MARKETPLACE
// -----------------------------

// GET /api/ebooks
export async function fetchEbooks({ q, category, language, limit, offset } = {}) {
  try {
    const qs = makeQS({ q, category, language, limit, offset });
    const url = `/ebooks${qs ? `?${qs}` : ""}`;
    const { data } = await API.get(url);

    // normalize response
    if (Array.isArray(data)) return { list: data, total: data.length, limit, offset };
    return {
      list: Array.isArray(data?.list) ? data.list : [],
      total: Number(data?.total ?? 0),
      limit: Number(data?.limit ?? limit ?? 20),
      offset: Number(data?.offset ?? offset ?? 0),
    };
  } catch (err) {
    throw normAxios(err, "Failed to load ebooks");
  }
}

export async function updateEbook(id, patch) {
  if (!id) throw new Error("id is required");
  try {
    const { data } = await API.patch(`/creator/ebooks/${id}`, patch);
    return data;
  } catch (err) {
    throw normAxios(err, "Failed to update ebook");
  }
}


// GET /api/ebooks/:id
// GET /api/ebooks/:slug
export async function fetchEbookBySlug(slug) {
  if (!slug) throw new Error("slug is required");
  try {
    const { data } = await API.get(`/ebooks/${encodeURIComponent(slug)}`);
    return data;
  } catch (err) {
    throw normAxios(err, "Failed to load ebook");
  }
}


// -----------------------------
// EBOOKS – CREATOR APIs
// -----------------------------

// GET /api/creator/ebooks/mine
export async function fetchMyEbooks() {
  try {
    const { data } = await API.get('/creator/ebooks/mine');
    return Array.isArray(data) ? data : data.list ?? data;
  } catch (err) {
    throw normAxios(err, 'Failed to load your ebooks');
  }
}

export async function fetchPendingEbooks({ q, category, limit, offset } = {}) {
  try {
    const qs = makeQS({ q, category, limit, offset });
    const { data } = await API.get(`/creator/ebooks/pending${qs ? `?${qs}` : ""}`);
    return data; // { list, total, limit, offset }
  } catch (err) {
    throw normAxios(err, "Failed to load pending ebooks");
  }
}


// POST /api/creator/ebooks
// payload: { title, description, pricePi, coverUrl, pages: [string] }
export async function createEbook({
  title,
  description = "",
  pricePi,
   priceUsd = null, // ✅ NEW (optional)
  coverUrl,
  category = "Other",
  tags = [],
  authors = [],
  language = "en",
  publishedYear,
  license = "unknown",
  sourceUrl = "",
  source,   // REQUIRED: { key, cdnUrl, bytes, contentType }
  preview,  // optional: { file: { key, bytes, contentType }, imageUrls?: [] }
}) {
  if (!title) throw new Error("title is required");
  if (pricePi == null) throw new Error("pricePi is required");
  // optional but validated if present
if (priceUsd != null) {
  const n = Number(priceUsd);
  if (!Number.isFinite(n) || n < 0) {
    throw new Error("priceUsd must be a non-negative number");
  }
}
  if (!source?.key) throw new Error("source upload is required");

  // sanity checks
  if (!source.contentType) throw new Error("source.contentType is required");
  if (!source.bytes || Number(source.bytes) <= 0) throw new Error("source.bytes is required");

  try {
 const { data } = await API.post("/creator/ebooks", {
  title,
  description,
  pricePi: Number(pricePi),
  priceUsd: priceUsd != null ? Number(priceUsd) : undefined, // ✅ add
  coverUrl,
  category,
  tags,
  authors,
  language,
  publishedYear: publishedYear != null ? Number(publishedYear) : undefined,
  license,
  sourceUrl,
  source,
  preview: preview ?? undefined,
});

    return data;
  } catch (err) {
    throw normAxios(err, "Failed to create ebook");
  }
}

// -----------------------------
// AUTHORS – PUBLIC
// -----------------------------

// GET /api/authors/:slug/ebooks
export async function fetchEbooksByAuthorSlug({ slug, limit, offset } = {}) {
  if (!slug) throw new Error("slug is required");
  try {
    const qs = makeQS({ limit, offset });
    const url = `/authors/${encodeURIComponent(slug)}/ebooks${qs ? `?${qs}` : ""}`;

    const { data } = await API.get(url);

    return {
      authorSlug: data?.authorSlug ?? slug,
      authorDisplayName: data?.authorDisplayName ?? slug,
      list: Array.isArray(data?.list) ? data.list : [],
      total: Number(data?.total ?? 0),
      limit: Number(data?.limit ?? limit ?? 20),
      offset: Number(data?.offset ?? offset ?? 0),
    };
  } catch (err) {
    throw normAxios(err, "Failed to load author ebooks");
  }
}

// POST /api/ebooks/me/accept-terms
export async function acceptEbookTerms() {
  try {
    const { data } = await API.post("/ebooks/me/accept-terms");
    return data; // { ok, terms:{ versionAccepted } }
  } catch (err) {
    throw normAxios(err, "Failed to accept terms");
  }
}


// (optional) admin-only if you wire role check
export async function publishEbook(bookId) {
  if (!bookId) throw new Error('bookId is required');
  try {
    const { data } = await API.post(`/creator/ebooks/${encodeURIComponent(bookId)}/publish`);
    return data;
  } catch (err) {
    throw normAxios(err, 'Failed to publish ebook');
  }
}

export async function unpublishEbook(bookId) {
  if (!bookId) throw new Error('bookId is required');
  try {
    const { data } = await API.post(`/creator/ebooks/${encodeURIComponent(bookId)}/unpublish`);
    return data;
  } catch (err) {
    throw normAxios(err, 'Failed to unpublish ebook');
  }
}

// -----------------------------
// EBOOKS – READER & PURCHASE
// -----------------------------

// GET /api/read/:id/pages?offset=&limit=
export async function fetchEbookPages({ bookId, offset = 0, limit = 1 }) {
  if (!bookId) throw new Error('bookId is required');

  try {
    const qs = makeQS({ offset, limit });
    const url = `/read/${encodeURIComponent(bookId)}/pages${qs ? `?${qs}` : ''}`;
    const { data } = await API.get(url);
    // shape: { bookId, title, totalPages, offset, limit, pages: [...] }
    return data;
  } catch (err) {
    throw normAxios(err, 'Failed to load ebook pages');
  }
}

// POST /api/ebooks/:id/purchase/record
export async function recordEbookPurchase({ bookId, paymentId, txid }) {
  if (!bookId) throw new Error('bookId is required');
  if (!paymentId) throw new Error('paymentId is required');
  if (!txid) throw new Error('txid is required');

  try {
    const { data } = await API.post(
      `/ebooks/${encodeURIComponent(bookId)}/purchase/record`,
      { paymentId, txid }
    );
    return data;
  } catch (err) {
    throw normAxios(err, 'Failed to record ebook purchase');
  }
}

// -----------------------------
// EBOOKS – METRICS (best effort)
// -----------------------------
export async function recordEbookView(bookId) {
  if (!bookId) return;
  try {
    await API.post(`/ebooks/${encodeURIComponent(bookId)}/view`);
  } catch {
    // best effort: ignore
  }
}

export async function recordEbookDownload(bookId) {
  if (!bookId) return;
  try {
    await API.post(`/ebooks/${encodeURIComponent(bookId)}/download`);
  } catch {
    // best effort: ignore
  }
}

// GET /api/read/:slug/preview-url  (public)
// GET /api/read/:slug/preview-url  (public)
export async function fetchEbookPreviewUrl(slug) {
  if (!slug) throw new Error("slug is required");
  try {
    const { data } = await API.get(`/read/${encodeURIComponent(slug)}/preview-url`);
    if (!data?.url) {
      throw new Error(data?.message || "No preview available");
    }
    return data.url;
  } catch (err) {
    throw normAxios(err, "No preview available");
  }
}


// GET /api/read/:id/file-url (JWT)
export async function fetchEbookFileUrl(bookId) {
  if (!bookId) throw new Error("bookId is required");
  try {
    const { data } = await API.get(`/read/${encodeURIComponent(bookId)}/file-url`);
    if (!data?.url) {
      throw new Error(data?.message || "No PDF uploaded yet");
    }
    return data.url;
  } catch (err) {
    throw normAxios(err, "Unable to download");
  }
}


// GET /api/read/:slug/free-file-url (PUBLIC, free only)
export async function fetchEbookFreeFileUrl(slug) {
  if (!slug) throw new Error("slug is required");
  try {
    const { data } = await API.get(`/read/${encodeURIComponent(slug)}/free-file-url`);
    if (!data?.url) {
      throw new Error(data?.message || "No PDF uploaded yet");
    }
    return data.url;
  } catch (err) {
    throw normAxios(err, "Unable to download");
  }
}


export async function presignEbookUpload({ filename, contentType, draftId = "draft", kind = "source" }) {
  const { data } = await API.post("/creator/ebooks/upload/presign", {
    filename,
    contentType,
    draftId,
    kind, // ✅ add
  }).catch((err) => { throw normAxios(err, "Failed to prepare ebook upload"); });

  return normalizePresignResponse(data);
}

export async function finalizeEbookUpload({ key, kind = "source" }) {
  const { data } = await API.post("/creator/ebooks/upload/finalize", { key, kind }).catch((err) => {
    throw normAxios(err, "Failed to finalize ebook upload");
  });
  return data;
}

function guessContentType(file) {
  const name = (file?.name || "").toLowerCase();

  if (name.endsWith(".epub")) return "application/epub+zip";
  if (name.endsWith(".pdf")) return "application/pdf";
  if (name.endsWith(".zip")) return "application/zip";

  // covers
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".webp")) return "image/webp";
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";

  return file?.type || "application/octet-stream";
}


export async function uploadEbookFile({ file, draftId = "draft", kind = "source", onProgress }) {
  const contentType = guessContentType(file);

  const pre = await presignEbookUpload({
    filename: file.name,
    contentType,
    draftId,
    kind,
  });

  const signedHeaders = { "Content-Type": contentType, ...(pre.signedHeaders || {}) };
  await putToS3({ url: pre.url, signedHeaders, file, onProgress });

 const fin = await finalizeEbookUpload({ key: pre.key, kind });

  return { key: pre.key, cdnUrl: fin.cdnUrl, bytes: fin.bytes, contentType: fin.contentType };
}

export async function uploadEbookCoverFile({ file, draftId = "draft", onProgress }) {
  return uploadEbookFile({ file, draftId, kind: "cover", onProgress });
}


export async function uploadEbookSourceFile({ file, draftId = "draft", onProgress }) {
  return uploadEbookFile({ file, draftId, kind: "source", onProgress });
}

export async function uploadEbookPreviewFile({ file, draftId = "draft", onProgress }) {
  return uploadEbookFile({ file, draftId, kind: "preview", onProgress });
}

export async function fetchMyLibrary() {
  try {
    const { data } = await API.get("/ebooks/me/library");
    return data; // { items: [...] }
  } catch (err) {
    throw normAxios(err, "Failed to load library");
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PARTNER ADMIN — add these to the bottom of utils/api/index.ts
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchAdminPartners({ limit = 20, offset = 0, active } = {}) {
  try {
    const qs = makeQS({ _limit: limit, _offset: offset, active });
    const { data } = await API.get(`/admin/partners${qs ? `?${qs}` : ""}`);
    return data; // { ok, list, total, limit, offset }
  } catch (err) {
    throw normAxios(err, "Failed to load partners");
  }
}

export async function activatePartner(id) {
  try {
    const { data } = await API.patch(`/admin/partners/${id}/activate`);
    return data;
  } catch (err) {
    throw normAxios(err, "Failed to activate partner");
  }
}

export async function deactivatePartner(id) {
  try {
    const { data } = await API.patch(`/admin/partners/${id}/deactivate`);
    return data;
  } catch (err) {
    throw normAxios(err, "Failed to deactivate partner");
  }
}

export async function rotatePartnerKey(id) {
  try {
    const { data } = await API.post(`/admin/partners/${id}/rotate-key`);
    return data; // { ok, apiKey, message }
  } catch (err) {
    throw normAxios(err, "Failed to rotate API key");
  }
}

export async function fetchPartnerUsage(id) {
  try {
    const { data } = await API.get(`/admin/partners/${id}/usage`);
    return data;
  } catch (err) {
    throw normAxios(err, "Failed to load partner usage");
  }
}
export async function fetchPartnerEarnings(id, month) {
  try {
    const qs = month ? `?month=${month}` : "";
    const { data } = await API.get(`/admin/partners/${id}/earnings${qs}`);
    return data;
  } catch (err) {
    throw normAxios(err, "Failed to load partner earnings");
  }
}
export const threadCanonicalPath = (id, slug) => `/community/thread/${id}/${slug}`;

export default API;